/**
 * PendingAceitesGate — modal bloqueante que aparece para empreendedores
 * que tem negocios com aceites LGPD pendentes.
 *
 * Fluxo:
 * 1. Ao logar (ou refresh), verifica GET /api/me/pending-aceites.
 * 2. Se houver >=1 negocio pendente, mostra modal bloqueante.
 * 3. Empreendedor le termos, marca 2 checkboxes, clica "Concordo".
 * 4. POST /api/me/pending-aceites/{id}/complete confirma para cada negocio.
 * 5. Modal fecha e libera navegacao.
 *
 * Comportamento default: bloqueia navegacao ate resolver.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, AlertTriangle, ExternalLink, Store } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { resolveBackendImage } from '../utils/brandLogo';

export default function PendingAceitesGate() {
  const { user, token } = useAuth();
  const [pending, setPending] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [aceitaTermos, setAceitaTermos] = useState(false);
  const [aceitaLgpd, setAceitaLgpd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      setPending([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/me/pending-aceites');
        if (cancelled) return;
        setPending(res.data?.items || []);
      } catch {
        if (!cancelled) setPending([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  if (!loaded || pending.length === 0) return null;

  const handleAccept = async () => {
    if (!aceitaTermos || !aceitaLgpd) {
      toast.error('Marque os dois checkboxes para continuar.');
      return;
    }
    setSubmitting(true);
    try {
      // Aceita em serie (evita race condition com counters)
      for (const biz of pending) {
        await api.post(`/me/pending-aceites/${biz.id}/complete`, {
          aceita_termos_uso: true,
          aceita_politica_privacidade: true,
        });
      }
      toast.success('Aceites confirmados! Bem-vindo.', { duration: 4000 });
      setPending([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao confirmar aceites.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasExpired = pending.some(b => b.expired);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="pending-aceites-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b bg-gradient-to-br from-[var(--primary)] to-purple-900 text-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold" data-testid="gate-title">Aceites pendentes</h2>
              <p className="text-sm text-white/90 mt-0.5">
                {pending.length === 1
                  ? 'Seu negocio foi cadastrado por um administrador. Confirme os aceites para continuar.'
                  : `Voce tem ${pending.length} negocios aguardando seus aceites LGPD.`}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {hasExpired && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              <div>
                <p className="font-semibold text-red-900">Prazo expirado</p>
                <p className="text-red-800">
                  Um ou mais negocios ultrapassaram o prazo de 30 dias e foram temporariamente
                  despublicados. Ao confirmar os aceites agora, eles voltarao a ficar visiveis.
                </p>
              </div>
            </div>
          )}

          {/* Lista de negocios */}
          <div className="space-y-2">
            {pending.map(biz => (
              <div key={biz.id}
                className="flex items-center gap-3 p-3 rounded-lg border bg-gray-50"
                data-testid={`pending-biz-${biz.id}`}
              >
                {biz.avatar_url ? (
                  <img src={resolveBackendImage(biz.avatar_url)} alt={biz.nome}
                    className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                    <Store className="w-5 h-5 text-[var(--primary)]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{biz.nome}</p>
                  <p className="text-xs text-gray-500">
                    {biz.expired
                      ? 'Prazo expirado'
                      : biz.days_remaining != null
                        ? `${biz.days_remaining} dia(s) restantes`
                        : 'Aguardando aceites'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Texto dos aceites */}
          <div className="text-sm text-gray-700 space-y-3 bg-gray-50 p-4 rounded-lg border">
            <p>
              Antes de comecar, precisamos que voce leia e concorde com os documentos abaixo. Isso e obrigatorio pela LGPD.
            </p>
            <div className="space-y-1">
              <a href="/termos-de-uso" target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-[var(--primary)] hover:underline text-sm font-medium"
                data-testid="link-termos">
                <ExternalLink className="w-3 h-3" /> Ler Termos de Uso completos
              </a>
              <a href="/politica-de-privacidade" target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-[var(--primary)] hover:underline text-sm font-medium"
                data-testid="link-lgpd">
                <ExternalLink className="w-3 h-3" /> Ler Politica de Privacidade / LGPD
              </a>
            </div>

            <div className="space-y-2 pt-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={aceitaTermos}
                  onChange={e => setAceitaTermos(e.target.checked)}
                  className="mt-1 accent-[var(--primary)]"
                  data-testid="checkbox-termos" />
                <span className="text-sm">
                  Eu li e <strong>concordo com os Termos de Uso</strong> da plataforma Empreende Comunidade.
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={aceitaLgpd}
                  onChange={e => setAceitaLgpd(e.target.checked)}
                  className="mt-1 accent-[var(--primary)]"
                  data-testid="checkbox-lgpd" />
                <span className="text-sm">
                  Eu li e <strong>concordo com a Politica de Privacidade</strong>, autorizando o
                  tratamento dos dados do(s) meu(s) negocio(s) conforme descrito.
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={handleAccept}
            disabled={!aceitaTermos || !aceitaLgpd || submitting}
            className="w-full py-3 bg-[var(--primary)] text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            data-testid="btn-confirmar-aceites"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Confirmando...</>
            ) : (
              <><ShieldCheck className="w-4 h-4" /> Concordo e continuar</>
            )}
          </button>
          <p className="text-[10px] text-center text-gray-500 mt-2">
            Sem sua confirmacao, a plataforma nao pode manter os dados do negocio. Voce pode revogar a qualquer momento em seu perfil.
          </p>
        </div>
      </div>
    </div>
  );
}
